const initParallaxItems = (scope = document) => {
  const parallaxItems = scope.querySelectorAll("[data-parallax]");
  if (!parallaxItems.length) return;
  if (window.__ttHomeParallaxBound) return;
  window.__ttHomeParallaxBound = true;

  let ticking = false;
  const parallaxTick = () => {
    const y = window.scrollY;
    document.querySelectorAll("[data-parallax]").forEach((el) => {
      const speed = Number(el.dataset.parallax) || 0.1;
      el.style.transform = `translateY(${Math.round(y * speed)}px)`;
    });
    ticking = false;
  };

  window.addEventListener(
    "scroll",
    () => {
      if (ticking) return;
      window.requestAnimationFrame(parallaxTick);
      ticking = true;
    },
    { passive: true }
  );
};

const initHeroSliders = (scope = document) => {
  scope.querySelectorAll("[data-hero-slider]").forEach((heroSlider) => {
    if (heroSlider.dataset.ttHomeInitialized === "true") return;
    heroSlider.dataset.ttHomeInitialized = "true";

    const slides = Array.from(heroSlider.querySelectorAll(".hero-slide"));
    const prevBtn = heroSlider.querySelector("[data-hero-prev]");
    const nextBtn = heroSlider.querySelector("[data-hero-next]");
    const dotsWrap = heroSlider.querySelector("[data-hero-dots]");
    let index = 0;
    let timerId;

    if (slides.length > 1 && dotsWrap) {
      dotsWrap.innerHTML = slides
        .map(
          (_, idx) =>
            `<button class="hero-dot${idx === 0 ? " is-active" : ""}" type="button" data-hero-dot="${idx}" aria-label="Go to slide ${idx + 1}"></button>`
        )
        .join("");
    }

    const dots = Array.from(heroSlider.querySelectorAll("[data-hero-dot]"));
    const renderSlide = () => {
      slides.forEach((slide, idx) => slide.classList.toggle("is-active", idx === index));
      dots.forEach((dot, idx) => dot.classList.toggle("is-active", idx === index));
    };
    const goTo = (nextIndex) => {
      const total = slides.length;
      index = (nextIndex + total) % total;
      renderSlide();
    };
    const startAuto = () => {
      clearInterval(timerId);
      timerId = setInterval(() => goTo(index + 1), 5000);
    };

    if (prevBtn) prevBtn.addEventListener("click", () => goTo(index - 1));
    if (nextBtn) nextBtn.addEventListener("click", () => goTo(index + 1));
    dots.forEach((dot) => {
      dot.addEventListener("click", () => goTo(Number(dot.dataset.heroDot)));
    });
    heroSlider.addEventListener("mouseenter", () => clearInterval(timerId));
    heroSlider.addEventListener("mouseleave", startAuto);

    renderSlide();
    startAuto();
  });
};

const initFeaturedCollectionsCarousels = (scope = document) => {
  scope.querySelectorAll("[data-featured-collections]").forEach((root) => {
    if (root.dataset.fcInitialized === "true") return;
    root.dataset.fcInitialized = "true";

    const track = root.querySelector("[data-featured-collections-track]");
    const controls = root.parentElement?.querySelector("[data-featured-collections-controls]");
    const prevBtn = controls?.querySelector("[data-featured-collections-prev]");
    const nextBtn = controls?.querySelector("[data-featured-collections-next]");
    const dotsWrap = controls?.querySelector("[data-featured-collections-dots]");
    if (!track || !track.classList.contains("tt-featured-collections__track--carousel")) return;

    const cards = Array.from(track.querySelectorAll(".tt-featured-collections__card"));
    if (cards.length <= 1 || !prevBtn || !nextBtn || !dotsWrap) {
      if (controls) controls.hidden = true;
      return;
    }

    let index = 0;
    let dots = [];
    let maxIndex = 0;
    let ticking = false;

    const getPerView = () => {
      const trackWidth = track.clientWidth || 1;
      const firstCard = cards[0]?.getBoundingClientRect().width || trackWidth;
      return Math.max(1, Math.round(trackWidth / Math.max(1, firstCard)));
    };
    const clampIndex = (value) => Math.max(0, Math.min(maxIndex, value));
    const updateButtons = () => {
      prevBtn.disabled = index <= 0;
      nextBtn.disabled = index >= maxIndex;
    };
    const updateDots = () => {
      dots.forEach((dot, idx) => dot.classList.toggle("is-active", idx === index));
    };
    const scrollToIndex = (nextIndex, smooth = true) => {
      index = clampIndex(nextIndex);
      track.scrollTo({
        left: cards[index]?.offsetLeft || 0,
        behavior: smooth ? "smooth" : "auto",
      });
      updateButtons();
      updateDots();
    };

    const buildDots = () => {
      const perView = getPerView();
      maxIndex = Math.max(0, cards.length - perView);
      dotsWrap.innerHTML = Array.from({ length: maxIndex + 1 })
        .map(
          (_, idx) =>
            `<button class="hero-dot${idx === index ? " is-active" : ""}" type="button" data-featured-collections-dot="${idx}" aria-label="Go to collections position ${
              idx + 1
            }"></button>`
        )
        .join("");
      dots = Array.from(dotsWrap.querySelectorAll("[data-featured-collections-dot]"));
      dots.forEach((dot) => {
        dot.addEventListener("click", () => {
          scrollToIndex(Number(dot.dataset.featuredCollectionsDot), true);
        });
      });
      updateButtons();
      updateDots();
    };

    const syncByScroll = () => {
      const left = track.scrollLeft;
      let closest = 0;
      let minDiff = Number.POSITIVE_INFINITY;
      for (let i = 0; i <= maxIndex; i += 1) {
        const diff = Math.abs((cards[i]?.offsetLeft || 0) - left);
        if (diff < minDiff) {
          minDiff = diff;
          closest = i;
        }
      }
      if (closest !== index) {
        index = closest;
        updateButtons();
        updateDots();
      }
    };

    prevBtn.addEventListener("click", () => scrollToIndex(index - 1, true));
    nextBtn.addEventListener("click", () => scrollToIndex(index + 1, true));
    track.addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          syncByScroll();
          ticking = false;
        });
      },
      { passive: true }
    );
    window.addEventListener("resize", () => {
      const current = index;
      buildDots();
      scrollToIndex(current, false);
    });

    buildDots();
    scrollToIndex(0, false);
  });
};

const initFeaturedCollectionsReveal = (scope = document) => {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
  const cards = scope.querySelectorAll("[data-featured-collections] .tt-featured-collections__card");
  if (!cards.length) return;

  if (reduced.matches) {
    cards.forEach((card) => card.classList.add("is-visible"));
    return;
  }

  if (!window.__ttFeaturedCollectionsObserver) {
    window.__ttFeaturedCollectionsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            window.__ttFeaturedCollectionsObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14 }
    );
  }

  cards.forEach((card) => {
    if (!card.classList.contains("is-visible")) {
      window.__ttFeaturedCollectionsObserver.observe(card);
    }
  });
};

const initTestimonialSliders = (scope = document) => {
  scope.querySelectorAll("[data-testimonial-slider]").forEach((testimonialSlider) => {
    if (testimonialSlider.dataset.ttHomeInitialized === "true") return;
    testimonialSlider.dataset.ttHomeInitialized = "true";

    const slides = Array.from(testimonialSlider.querySelectorAll(".testimonial-slide"));
    const track = testimonialSlider.querySelector(".testimonial-track");
    const prevBtn = testimonialSlider.querySelector("[data-testimonial-prev]");
    const nextBtn = testimonialSlider.querySelector("[data-testimonial-next]");
    const dotsWrap = testimonialSlider.querySelector("[data-testimonial-dots]");
    if (!track || slides.length <= 1) {
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
      if (dotsWrap) dotsWrap.hidden = true;
      return;
    }

    let index = 0;
    let perView = 3;
    let timerId;
    const dots = [];

    const getPerView = () => {
      if (window.innerWidth <= 740) return 1;
      if (window.innerWidth <= 1100) return 2;
      return 3;
    };
    const getMaxIndex = () => Math.max(0, slides.length - perView);
    const syncDots = () => {
      dots.forEach((dot, idx) => dot.classList.toggle("is-active", idx === index));
    };
    const buildDots = () => {
      if (!dotsWrap) return;
      const count = getMaxIndex() + 1;
      dotsWrap.innerHTML = Array.from({ length: count })
        .map(
          (_, idx) =>
            `<button class="hero-dot${idx === 0 ? " is-active" : ""}" type="button" data-testimonial-dot="${idx}" aria-label="Go to testimonial position ${idx + 1}"></button>`
        )
        .join("");
      dots.length = 0;
      dots.push(...Array.from(testimonialSlider.querySelectorAll("[data-testimonial-dot]")));
      dots.forEach((dot) => dot.addEventListener("click", () => goTo(Number(dot.dataset.testimonialDot))));
    };
    const render = () => {
      track.style.setProperty("--cards-per-view", String(perView));
      const gap = Number.parseFloat(getComputedStyle(track).gap) || 14;
      const slideWidth = slides[0].getBoundingClientRect().width;
      const offset = index * (slideWidth + gap);
      track.style.transform = `translateX(${-offset}px)`;
      syncDots();
    };
    const goTo = (nextIndex) => {
      const maxIndex = getMaxIndex();
      if (nextIndex > maxIndex) {
        index = 0;
      } else if (nextIndex < 0) {
        index = maxIndex;
      } else {
        index = nextIndex;
      }
      render();
    };
    const startAuto = () => {
      clearInterval(timerId);
      timerId = setInterval(() => goTo(index + 1), 5000);
    };
    const handleResize = () => {
      const nextPerView = getPerView();
      if (nextPerView !== perView) {
        perView = nextPerView;
        index = Math.min(index, getMaxIndex());
        buildDots();
      }
      render();
    };

    if (prevBtn) prevBtn.addEventListener("click", () => goTo(index - 1));
    if (nextBtn) nextBtn.addEventListener("click", () => goTo(index + 1));
    testimonialSlider.addEventListener("mouseenter", () => clearInterval(timerId));
    testimonialSlider.addEventListener("mouseleave", startAuto);
    window.addEventListener("resize", handleResize);

    perView = getPerView();
    buildDots();
    render();
    startAuto();
  });
};

const initHomeRuntime = (scope = document) => {
  initParallaxItems(scope);
  initHeroSliders(scope);
  initFeaturedCollectionsCarousels(scope);
  initFeaturedCollectionsReveal(scope);
  initTestimonialSliders(scope);
};

initHomeRuntime(document);
document.addEventListener("shopify:section:load", (event) => {
  initHomeRuntime(event?.target || document);
});
